<template>
  <v-card class="copier-cart-card d-flex flex-column h-100" rounded="xl" elevation="0">
    <div class="cart-header d-flex align-center justify-space-between pb-4 border-bottom">
      <div>
        <h3 class="text-h6 font-weight-black text-slate-800 d-flex align-center">
          <v-icon icon="mdi-printer-3d-nozzle-outline" color="primary" class="mr-2" size="24" />
          Selected Equipment
        </h3>
        <p class="text-caption text-slate-500 mb-0">Review your customized configurations</p>
      </div>
      <v-chip color="primary" size="small" variant="flat" class="font-weight-black">
        {{ cart_items.length }} Item{{ cart_items.length > 1 ? 's' : '' }}
      </v-chip>
    </div>

    <!-- Cart Items Scroll Area -->
    <div class="flex-grow-1 overflow-y-auto pr-1 py-4 cart-items-list" style="max-height: 480px;">
      <div v-for="(item, idx) in cart_items" :key="idx" class="cart-item-row mb-4 pa-4 rounded-xl">
        <div class="d-flex align-start">
          <v-avatar size="50" class="mr-4 img-avatar bg-white" rounded="lg">
            <img v-if="item.product.product_image" :src="ProductImage(item.product)" alt="Image" style="width: 100%; height: 100%; object-fit: contain;">
            <v-icon v-else color="slate-400" size="36">mdi-printer</v-icon>
          </v-avatar>
          <div class="flex-grow-1 min-width-0">
            <div class="d-flex justify-space-between align-start mb-1">
              <div class="min-width-0 pr-2">
                <h4 class="text-subtitle-2 mt-0 font-weight-black text-slate-800 truncate mb-0">{{ item.product.name }}</h4>
                <div class="text-caption font-weight-bold text-slate-400">{{ item.product.printer_category || 'Multifunction Printer' }}</div>
              </div>
              <div class="d-flex align-center gap-2 flex-shrink-0 ml-2">
                <v-btn
                  icon="mdi-pencil-outline"
                  variant="text"
                  density="comfortable"
                  size="small"
                  color="slate-500"
                  title="Adjust configuration"
                  class="pa-1"
                  @click="configurator_step = 'configurator'"
                />
                <v-btn
                  v-if="cart_items.length > 1"
                  icon="mdi-close"
                  variant="text"
                  density="comfortable"
                  size="small"
                  color="red-darken-1"
                  title="Remove equipment"
                  class="pa-1"
                  @click="$emit('remove-item', idx)"
                />
              </div>
            </div>

            <!-- Selected Accessories list -->
            <div v-if="item.datasets.some((d: any) => d.selected)" class="accessories-list mt-2 mb-2">
              <div
                v-for="ds in item.datasets.filter((d: any) => d.selected)"
                :key="ds.id"
                class="accessory-row d-flex align-center gap-2 py-2 px-3"
              >
                <v-icon icon="mdi-check-circle" size="16" color="primary" class="flex-shrink-0" />
                <div class="text-caption font-weight-bold text-slate-800 text-truncate" style="min-width: 0;">{{ Accessory(item, ds).name }}</div>
              </div>
            </div>

            <div class="d-flex justify-space-between align-center mt-2 pt-2 border-top-dashed" v-if="ConfigShowPricing && form.acquisition_type === 'lease' && !isItemUnpriced(item)">
              <span class="text-caption font-weight-bold text-slate-500">Estimated Price</span>
              <span class="text-caption font-weight-black text-primary">
                ${{ CalculateProductMonthlyPrice(item.product, item.datasets, item.accessories) }}<small class="font-weight-medium text-slate-400">/mo</small>
              </span>
            </div>
            <div class="d-flex justify-space-between align-center mt-2 pt-2 border-top-dashed" v-else-if="form.acquisition_type === 'lease'">
              <span class="text-caption font-weight-bold text-slate-500">Volume Allowance</span>
              <span class="text-caption font-weight-black text-slate-800">
                {{ (Number(item.product?.prints_per_month || 0) + Number(item.product?.color_volume || 0)).toLocaleString() }} pages/mo
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Receipt Summary Block -->
    <div class="receipt-block pa-5 rounded-xl mt-auto">
      <div class="d-flex justify-space-between align-center mb-2" v-if="form.acquisition_type === 'lease'">
        <span class="text-caption font-weight-bold text-slate-500">Lease Term</span>
        <span class="text-body-2 font-weight-black text-slate-800">{{ form.lease_term }} months</span>
      </div>
      <div class="d-flex justify-space-between align-center mb-2" v-else>
        <span class="text-caption font-weight-bold text-slate-500">Acquisition</span>
        <span class="text-body-2 font-weight-black text-slate-800">Full purchase</span>
      </div>
      <div class="d-flex justify-space-between align-center mb-2" v-if="form.acquisition_type === 'lease'">
        <span class="text-caption font-weight-bold text-slate-500">Service &amp; Toner</span>
        <span class="text-caption font-weight-bold text-green-darken-3">Included</span>
      </div>
      <div class="d-flex justify-space-between align-center mb-2" v-if="free_shipping">
        <span class="text-caption font-weight-bold text-slate-500">Delivery &amp; Setup</span>
        <span class="text-caption font-weight-bold text-green-darken-3">Free</span>
      </div>

      <div class="border-top-dashed my-3"></div>

      <div class="d-flex justify-space-between align-center pt-1">
        <div>
          <span class="text-subtitle-2 font-weight-black text-slate-800 d-block">
            {{ ConfigShowPricing && form.acquisition_type === 'lease' && !isCartUnpriced ? 'Estimated Total' : 'Equipment Quote' }}
          </span>
          <span class="text-caption text-slate-400">
            {{ ConfigShowPricing && form.acquisition_type === 'lease' && !isCartUnpriced ? 'Includes standard delivery & setup' : 'Formal pricing sent after review' }}
          </span>
        </div>
        <div class="text-right">
          <span v-if="ConfigShowPricing && form.acquisition_type === 'lease' && !isCartUnpriced" class="text-h5 font-weight-black text-primary d-block">
            ${{ cartTotalMonthlyPrice }}<span class="text-caption font-weight-bold text-slate-400">/mo</span>
          </span>
          <span v-else class="text-caption font-weight-black text-green-darken-3 bg-green-lighten-5 pa-2 py-1 rounded d-inline-block">
            Free Quote
          </span>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import { CalculateProductMonthlyPrice, IsProductCustomQuote } from '@/mixins/PricingMixins.ts'

defineProps<{
  cartTotalMonthlyPrice?: number
}>()

defineEmits<{
  (e: 'remove-item', idx: number): void
}>()

const store = useGlobalsStore()
const { form, cart_items, configurator_step, free_shipping, ConfigShowPricing } = storeToRefs(store)

const ProductImage = store.ProductImage

const isCartUnpriced = computed(() => {
  return cart_items.value.every((item: any) => IsProductCustomQuote(item.product))
})

const isItemUnpriced = (item: any) => {
  return IsProductCustomQuote(item?.product)
}

function Accessory(item: any, dataset: any) {
  return item.accessories.find((acc: any) => acc.id === dataset.accessory_id) || { name: '', part_number: '', advertising_price: 0, description: '' }
}
</script>

<style scoped lang="scss">
.copier-cart-card {
  background: white;
  border: 1px solid rgba(226, 232, 240, 0.8) !important;
  box-shadow: 0 10px 30px -10px rgba(148, 163, 184, 0.15) !important;
  padding: 28px;
}
.cart-header {
  border-bottom: 1px solid #f1f5f9;
}
.text-slate-800 {
  color: #1e293b !important;
}
.text-slate-500 {
  color: #64748b !important;
}
.text-slate-400 {
  color: #94a3b8 !important;
}
.cart-item-row {
  background-color: #f8fafc;
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    transform: translateY(-2px);
  }
}
.img-avatar {
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}
.accessories-list {
  border-left: 2px solid #2563eb;
  border-radius: 4px;
  overflow: hidden;
}
.accessory-row {
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
}
.border-top-dashed {
  border-top: 1px dashed #cbd5e1 !important;
}
.receipt-block {
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
}
</style>
